import * as React from 'react';
import styled, { keyframes } from 'styled-components';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import { variant } from 'styled-system';
import { Elevation, MediaQueries } from '../../essentials';
import { getSemanticValue } from '../../utils/cssVariables';
import { get } from '../../utils/themeGet';
import { Text } from '../Text/Text';
import { DarkScheme, LightScheme } from '../ColorScheme';
import { useClosestColorScheme } from '../../utils/hooks/useClosestColorScheme';
const fadeAnimation = keyframes `
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
`;
const arrowPlacementStyles = variant({
    variants: {
        bottom: {
            right: 'calc(50% - 0.25rem)'
        },
        'bottom-end': {
            right: '0.3rem'
        },
        'top-start': {
            bottom: '-0.5rem',
            transform: 'rotate(-180deg)'
        },
        top: {
            bottom: '-0.5rem',
            transform: 'rotate(-180deg)',
            right: 'calc(50% - 0.25rem)'
        },
        'top-end': {
            bottom: '-0.5rem',
            transform: 'rotate(-180deg)',
            right: '0.3rem'
        },
        left: {
            top: 'calc(50% - 0.25rem)',
            left: 'auto',
            right: '-0.5rem',
            transform: 'rotate(90deg)'
        },
        'left-end': {
            bottom: '0.5rem',
            left: 'auto',
            right: '-0.5rem',
            transform: 'rotate(90deg)'
        },
        'left-start': {
            top: '0.5rem',
            left: 'auto',
            right: '-0.5rem',
            transform: 'rotate(90deg)'
        },
        right: {
            top: 'calc(50% - 0.25rem)',
            left: '-0.25rem',
            right: 'auto',
            transform: 'rotate(-90deg)'
        },
        'right-end': {
            bottom: '0.5rem',
            left: '-0.25rem',
            right: 'auto',
            transform: 'rotate(-90deg)'
        },
        'right-start': {
            top: '0.5rem',
            left: '-0.25rem',
            right: 'auto',
            transform: 'rotate(-90deg)'
        }
    }
});
const TooltipBody = styled.div.withConfig({ displayName: "TooltipBody", componentId: "sc-7ll1gl" }) `
    position: relative;
    z-index: ${Elevation.TOOLTIP};
    background-color: ${getSemanticValue('background-backdrop')};
    color: ${getSemanticValue('background-page-default')};
    padding: 0.25rem 0.5rem;
    border-radius: ${get('radii.2')};
    opacity: 0;
    animation-name: ${fadeAnimation};
    animation-duration: 100ms;
    animation-fill-mode: forwards;
    animation-timing-function: ease-in;
    animation-delay: 400ms;
    max-width: 10rem;

    ${MediaQueries.small} {
        max-width: 18.75rem;
    }

    &::after {
        bottom: 100%;
        content: ' ';
        height: 0;
        width: 0;
        position: absolute;
        pointer-events: none;
        border: 0.25rem solid rgba(0, 0, 0, 0);
        border-bottom-color: ${getSemanticValue('background-backdrop')};
        margin-left: -0.25rem;

        ${arrowPlacementStyles}
    }
`;
const Tooltip = ({ content, children, placement = 'top', alwaysVisible = false }) => {
    var _a;
    const [isVisible, setIsVisible] = React.useState(alwaysVisible);
    const [triggerReference, setTriggerReference] = React.useState(undefined);
    const [contentReference, setContentReference] = React.useState(undefined);
    const { styles, attributes } = usePopper(triggerReference, contentReference, {
        placement,
        modifiers: [
            {
                name: 'offset',
                enabled: true,
                options: {
                    offset: [0, 5]
                }
            }
        ]
    });
    const enforcedColorScheme = useClosestColorScheme(triggerReference);
    const PortalWrapper = React.useMemo(() => {
        if (!enforcedColorScheme)
            return React.Fragment;
        return enforcedColorScheme === 'light' ? LightScheme : DarkScheme;
    }, [enforcedColorScheme]);
    let dynamicContent = content;
    if (typeof content === 'string') {
        dynamicContent = (React.createElement(Text, { as: "p", fontSize: 0 }, content));
    }
    const handleVisibilityChange = (nextState) => {
        if (!alwaysVisible) {
            setIsVisible(nextState);
        }
    };
    return (React.createElement(React.Fragment, null,
        React.cloneElement(children, {
            onMouseOver: () => handleVisibilityChange(true),
            onMouseOut: () => handleVisibilityChange(false),
            ref: setTriggerReference
        }),
        content &&
            isVisible &&
            createPortal(React.createElement(PortalWrapper, null,
                React.createElement(TooltipBody, Object.assign({ ref: setContentReference, style: Object.assign({}, styles.popper), variant: (_a = attributes.popper) === null || _a === void 0 ? void 0 : _a['data-popper-placement'] }, attributes.popper), dynamicContent)), document.body)));
};
export { Tooltip };
